'use client';

import { c as _c } from "react/compiler-runtime";
import { ErrorBoundary } from '@datadog/browser-rum-react';
import { useTranslations } from 'next-intl';

/**
 * DatadogErrorBoundary component for catching and reporting React errors
 *
 * This component wraps your application to catch JavaScript errors anywhere in the
 * child component tree and automatically report them to Datadog RUM.
 *
 * @example
 * ```tsx
 * <DatadogErrorBoundary>
 *   <YourApp />
 * </DatadogErrorBoundary>
 * ```
 */
export default function DatadogErrorBoundary(t0) {
  const $ = _c(5);
  const {
    children
  } = t0;
  const t = useTranslations("DatadogErrorBoundary");
  let t1;
  if ($[0] !== t) {
    t1 = t2 => {
      const {
        error,
        resetError: handleResetError
      } = t2;
      return <div className="flex min-h-screen items-center justify-center bg-gray-50"><div className="rounded-lg bg-white p-8 shadow-lg"><h2 className="mb-4 text-xl font-semibold text-gray-900">{t("somethingWentWrong")}</h2><p className="mb-4 text-gray-600">{t("anErrorOccurred")}</p><details className="mb-4"><summary className="cursor-pointer text-sm text-gray-500">{t("errorDetails")}</summary><pre className="mt-2 rounded bg-gray-100 p-2 text-xs text-gray-700">{error?.message || t("unknownError")}</pre></details><button onClick={handleResetError} aria-label={t("tryAgain")} tabIndex={0} className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none">{t("tryAgain")}</button></div></div>;
    };
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <ErrorBoundary fallback={t1}>{children}</ErrorBoundary>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}